import { PaginatedSchema } from "@shared/api/pagination";
import axiosClient from "../../../shared/lib/http/axiosClient";
import { Account, AccountAdd, AccountEdit, AccountSchema, AccountsPaginatedWithRelationsSchema, AccountsResponse, AccountsResponseWithRelations, AccountSearch } from "@models/account";
import { AccountCustomDataSearch } from "@models/account-custom-data";

const AccountsPaginatedSchema = PaginatedSchema(AccountSchema);


type AccountInclude =
  | 'customData'
  | 'entries'
  | 'entries.document'
  | 'entries.document.documentSettings'
  ;


export type AccountSortField =
  | 'id'
  | 'account_name'
  | 'account_number'
  | 'created_at'
  | 'updated_at'
  ;

type AccountCustomDataSortField = `custom_data.${number}.field_value`;
type AccountCustomDataSort = AccountCustomDataSortField | `-${AccountCustomDataSortField}`;

export type AccountSort = AccountSortField | `-${AccountSortField}` | AccountCustomDataSort;

const ACCOUNT_SORT_FIELDS: AccountSortField[] = [
  'id',
  'account_name',
  'account_number',
  'created_at',
  'updated_at',
];

const CUSTOM_DATA_SORT_PATTERN = /^custom_data\.\d+\.field_value$/;


/**
 * - include - which relations to include in the response. for example: ["customData", "entries.document"] will include the custom data of the accounts, and the documents of the entries of the accounts.
 * - sort - which fields to sort by, and in which direction. for example: ["account_name", "-created_at"] will sort by account_name ascending, and then by created_at descending.
 * - per_page - how many records to return per page. if not provided, defaults to 20. if set to 0, will return all records without pagination.
 * - cursor - the cursor for pagination. if not provided, will return the first page.
 */
interface IndexParams {
  include?: AccountInclude[],
  sort?: AccountSort[],
  per_page?: number,
  cursor?: string,
}


/**
 * Generates a query string for the API request based on the provided parameters.
 * @param params IndexParams object
 * @returns string
 */
function getParamString(params: IndexParams) {

  const { include, sort, per_page = 20, cursor } = params;

  return {
    params: {
      ...(include?.length  && { include: include.join(',') }),
      ...(sort?.length     && { sort: sort.join(',') }),
      ...(per_page         && { per_page }),
      ...(cursor           && { cursor }),
    },
  };
  
}


/**
 * Fetches a list of accounts with pagination and filtering options.
 * @param params IndexParams object containing include, sort, per_page, and cursor parameters.
 * @returns accounts response with pagination metadata
 */
export const getAccounts = async (params: IndexParams = {}): Promise<AccountsResponse> => {
    
  const response = await axiosClient.get(
    `/api/accounts`, getParamString(params)
  );

  return AccountsPaginatedSchema.parse(response.data);
};



/**
 * Fetches a list of accounts with relations.
 * @returns accounts response with relations and pagination metadata
 */
export const getCustomersWithRelations = async (): Promise<AccountsResponseWithRelations> => {
  const accounts = await getAccounts({ include: ["customData", "entries.document.documentSettings"], per_page: 10 });
  return AccountsPaginatedWithRelationsSchema.parse(accounts);
};


/**
 * Normalizes the custom_data filter for account search.
 * Add field_index if missing, and removes objects with empty field_value.
 * @param customDataArray custom_data array from AccountSearch filter
 * @returns normalized custom_data array or undefined if empty/invalid
 */
function normalizeAccountSearchFilterCustomData(customDataArray: AccountSearch['custom_data'] | undefined): AccountCustomDataSearch[] | undefined {
  
  if (!customDataArray?.length) {
    return undefined;
  }

  const out: AccountCustomDataSearch[] = [];

  customDataArray.forEach((item, i) => {

    const value = typeof item.field_value === "string" ? item.field_value.trim() : "";

    if (value === "") {
      return;
    }

    const fieldIndex = (
      item.field_index !== undefined &&
      item.field_index !== null &&
      Number.isFinite(Number(item.field_index))
    )? Number(item.field_index) : i;

    out.push({ field_index: fieldIndex, field_value: value });
  });

  return out.length > 0 ? out : undefined;
}


/**
 * handale custom_data for account search filter
 * @param filter 
 * @returns 
 */
function normalizeAccountSearchFilter(filter: AccountSearch): AccountSearch {
  const custom_data = normalizeAccountSearchFilterCustomData(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;
  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}



/**
 * - filter - fields to filter by (Spatie `filter[...]` keys in the POST body).
 * - include - which relations to include in the response.
 * - sort - which fields to sort by, and in which direction. for example: ["account_name", "-created_at"].
 * - per_page - how many records to return per page. defaults to 20. max 100 on the server.
 * - cursor - the cursor for pagination. if not provided, returns the first page.
 */
export interface SearchParams {
  filter?: Partial<AccountSearch>,
  include?: AccountInclude[],
  sort?: AccountSort[],
  per_page?: number,
  cursor?: string,
}

/**
 * Builds the POST body for `/api/accounts/search` (Spatie Query Builder format).
 * @param params SearchParams object containing filter, include, sort, per_page, and cursor.
 * @returns object to be sent in the POST body
 */
function getSearchBody(params: SearchParams) {

  const { filter: rawFilter, include, sort, per_page = 20, cursor } = params;
  const filter = rawFilter ? normalizeAccountSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length  && { include: include.join(',') }),
    ...(sort?.length     && { sort: sort.join(',') }),
    ...(per_page         && { per_page }),
    ...(cursor           && { cursor }),
  };

}

/**
 * Searches accounts with filters, sorting, pagination, and optional includes.
 * @param params SearchParams object containing filter, include, sort, per_page, and cursor.
 * @returns accounts response with pagination metadata
 */
const ACCOUNT_BROWSE_INCLUDES: AccountInclude[] = [
  "customData",
  "entries.document.documentSettings",
];

const ACCOUNT_BROWSE_PER_PAGE = 10;

/**
 * Searches accounts with filters, sorting, pagination, and optional includes.
 * @param params SearchParams object containing filter, include, sort, per_page, and cursor.
 * @returns accounts response with pagination metadata
 */
export const searchAccounts = async (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => {

  const response = await axiosClient.post(
    `/api/accounts/search`,
    getSearchBody(params),
    { signal },
  );

  return AccountsPaginatedSchema.parse(response.data);
};

function searchAccountsBrowse(
  accountType: AccountSearch["account_type"],
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> {
  const { filter, ...rest } = params;
  return searchAccounts(
    {
      filter: { account_type: accountType, ...filter },
      include: ACCOUNT_BROWSE_INCLUDES,
      per_page: ACCOUNT_BROWSE_PER_PAGE,
      ...rest,
    },
    signal,
  );
}

/**
 * Maps a sort field and direction to Spatie's sort format (`-field` for DESC).
 */
export const toAccountSort = (field: AccountSortField, order: 'ASC' | 'DESC'): AccountSort =>
  order === 'DESC' ? `-${field}` : field;


/**
 * Maps a browse-table sort key and direction to Spatie's sort format.
 * Strips any existing `-` prefix before applying direction.
 */
export const buildAccountSort = (sortKey: string, order: 'ASC' | 'DESC'): AccountSort => {
  const bare = sortKey.replace(/^-/, '');

  if (ACCOUNT_SORT_FIELDS.includes(bare as AccountSortField)) {
    return toAccountSort(bare as AccountSortField, order);
  }

  if (CUSTOM_DATA_SORT_PATTERN.test(bare)) {
    return (order === 'DESC' ? `-${bare}` : bare) as AccountSort;
  }

  return toAccountSort('id', order);
};


/**
 * Fetches a list of accounts with relations.
 * @returns accounts response with relations and pagination metadata
 */
export const searchCustomersWithRelations = async (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponseWithRelations> => {
  const accounts = await searchAccountsBrowse("לקוח-ספק", params, signal);
  return AccountsPaginatedWithRelationsSchema.parse(accounts);
};

export const searchIncomesBrowse = (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => searchAccountsBrowse("הכנסה", params, signal);

export const searchOutcomesBrowse = (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => searchAccountsBrowse("הוצאה", params, signal);

export const searchBanksBrowse = (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => searchAccountsBrowse("בנק", params, signal);

export const searchSystemsBrowse = (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => searchAccountsBrowse("מערכת", params, signal);

export const searchCashBoxesBrowse = (
  params: SearchParams = {},
  signal?: AbortSignal,
): Promise<AccountsResponse> => searchAccountsBrowse("קופה", params, signal);


export const getAccountById = async (id: string | number, signal?: AbortSignal): Promise<Account> => {
  
  const response = await axiosClient.get(
    `/api/accounts/${id}`,
    {signal}
  );
  
  return response.data;
};


export const getNextAccountNumber = async (accountType: string): Promise<number> => {
  
  const response = await axiosClient.post(
    "/api/accounts/next-number",
    {account_type: accountType}
  );

  return response.data.next_account_index;
};


export const addAccount = async (accountToAdd: AccountAdd): Promise<Account> => {
  
  const response = await axiosClient.post(
    "/api/accounts",
    accountToAdd
  );

  return response.data;
};


export const saveAccount = async (accountId: number, accountToSave: AccountEdit): Promise<Account> => {

  const response = await axiosClient.patch(
    `/api/accounts/${accountId}`,
    accountToSave
  );

  return response.data;
};


export const deleteAccount = async (accountId: number): Promise<void> => {
  await axiosClient.delete(
    `/api/accounts/${accountId}`
  );
};


export const accountRecommendation = async (sortCode: number): Promise<number> => {
  
  const response = await axiosClient.post(
    "/api/accounts/recommendation",
    { sort_code: sortCode }
  );
  
  return response.data.recommended_account_number;
};